import { settings } from "@/settings";
import { getOperator, globalConfig, headers, UON } from "@/utils";
import axios from "axios";
import { api } from "@/auth/api";
import i18n from "@/i18n";

// LOCAL: pointe directement sur le service esign local

const basePathV2 = `${settings.api_url}/${UON.SystemUid.odmContactMechanism}/api/1/odm-contact_mechanisme/mailconfig`;
const basePathV3 = `${settings.api_url}/${UON.SystemUid.odmContactMechanism}/api/1/model/odm-contactmechanism/emailtemplate`;
const basePathEsign = `${settings.api_url}/${UON.SystemUid.odmEsign}/api/1/${UON.SystemUid.odmEsign}/esignemailconfig`;

function toBoolean(value: any): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  return false;
}

function parseBusinessData(raw: any): Record<string, any> {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return {};

  const trimmed = raw.trim();
  if (!trimmed) return {};

  try {
    return JSON.parse(trimmed);
  } catch (_e) {
    return {};
  }
}

function getTemplateBusinessData(templateData: any): Record<string, any> {
  return parseBusinessData(
    templateData?.businessData ||
    templateData?._business_data ||
    templateData?.business_data,
  );
}

function getTemplateResourceUid(templateData: any, fallbackResourceUid?: string): string {
  return String(
    templateData?.resourceUid ||
    templateData?.templateUON ||
    templateData?.templateUon ||
    fallbackResourceUid ||
    "",
  ).trim();
}

async function getEsignIdByEmailTemplateId(templateResourceUid: string): Promise<string | null> {
  const uid = String(templateResourceUid || "").trim();
  if (!uid) return null;

  try {
    const response = await api().get(
      `${basePathEsign}/by-email-template/${encodeURIComponent(uid)}/id/`,
    );
    const value = String(response?.data || "").trim();
    return value || null;
  } catch (_e) {
    return null;
  }
}

async function deleteEsignById(esignId: string): Promise<void> {
  const id = String(esignId || "").trim();
  if (!id) return;
  await api().delete(`${basePathEsign}/${encodeURIComponent(id)}/`);
}

async function deleteEsignByEmailTemplateId(templateResourceUid: string): Promise<void> {
  const uid = String(templateResourceUid || "").trim();
  if (!uid) return;
  await api().delete(`${basePathEsign}/by-email-template/${encodeURIComponent(uid)}/`);
}

function buildEsignPayload(templatePayload: any, templateData: any) {
  const businessData = parseBusinessData(templatePayload?.businessData || templateData?.businessData);
  const messageType = String(businessData?.messageType || "").trim();
  const emailTemplateSystemUid = String(templateData?.systemUid || templatePayload?.systemUid || "odm-contactmechanism").trim();
  const emailTemplateResourceUid = String(templateData?.resourceUid || templatePayload?.resourceUid || "").trim();

  if (!emailTemplateResourceUid) {
    throw new Error("Cannot create e-sign config without template resourceUid");
  }

  return {
    objectType: "odm.esign.esignemailconfig",
    messageType: {
      objectType: "odm.esign.esignmessagetype",
      systemUid: "odm-esign",
      resourceUid: messageType,
    },
    emailTemplate: {
      objectType: "odm.contactmechanism.emailtemplate",
      systemUid: emailTemplateSystemUid,
      resourceUid: emailTemplateResourceUid,
    },
    active: toBoolean(businessData?.active ?? templatePayload?.active ?? templateData?.active),
    businessData: {},
  };
}

async function syncDefaultTemplateToEsign(templatePayload: any, templateData: any) {
  const businessData = parseBusinessData(templatePayload?.businessData || templateData?.businessData);
  if (!toBoolean(businessData?.isDefault)) return;
  if (!toBoolean(businessData?.active ?? templatePayload?.active ?? templateData?.active)) return;
  const messageType = String(businessData?.messageType || "").trim();
  if (!messageType) return;
  const esignPayload = buildEsignPayload(templatePayload, templateData);
  await api().post(`${basePathEsign}/`, esignPayload);
}

async function syncDefaultTemplateState(templatePayload: any, templateData: any) {
  const businessData = parseBusinessData(templatePayload?.businessData || templateData?.businessData);
  const isDefault = toBoolean(businessData?.isDefault);
  const isActive = toBoolean(businessData?.active ?? templatePayload?.active ?? templateData?.active);
  const templateUid = getTemplateResourceUid(templateData, templatePayload?.resourceUid);

  if (!templateUid) return;

  if (isDefault && isActive) {
    const messageType = String(businessData?.messageType || "").trim();
    if (!messageType) return;
    const esignPayload = buildEsignPayload(templatePayload, templateData);
    await api().post(`${basePathEsign}/`, esignPayload);
    return;
  }

  const esignId = await getEsignIdByEmailTemplateId(templateUid);
  if (esignId) {
    await deleteEsignById(esignId);
  }
}

function buildDefaultSubject(emailConfig: any): string {
  const businessData = emailConfig?.businessData || {};
  const rawMessageType = businessData?.messageType || "";
  const messageType = String(rawMessageType).trim().toUpperCase();
  const documentType = String(businessData?.documentType || "").trim();
  const t = i18n.global.t.bind(i18n.global);

  if (messageType === "INVITATION") {
    return documentType
      ? String(t("emailConfig.subject.invitation.withDocument", { document: documentType }))
      : String(t("emailConfig.subject.invitation.base"));
  }

  if (messageType === "CONFIRMATION") {
    return documentType
      ? String(t("emailConfig.subject.confirmation.withDocument", { document: documentType }))
      : String(t("emailConfig.subject.confirmation.base"));
  }

  if (messageType === "REFUSAL") {
    return documentType
      ? String(t("emailConfig.subject.refusal.withDocument", { document: documentType }))
      : String(t("emailConfig.subject.refusal.base"));
  }

  if (rawMessageType) return String(rawMessageType);
  return String(t("emailConfig.subject.default"));
}

function normalizeTemplatePayload(emailConfig: any): any {
  const { _business_data, ...payload } = emailConfig || {};

  return {
    ...payload,
    sender: (payload?.sender || "no-reply@teamwillgroup.com").trim(),
    recipient: (payload?.recipient || "recipient@teamwillgroup.com").trim(),
    subject: (payload?.subject || buildDefaultSubject(payload)).trim(),
  };
}
export function getQcSearchEmailConfig(filter: any, offset?: any, limit?: any): any {
  const qc_header: any = {
    qc: {
      queryId: "email-config-filter-search",
      offset: offset || 0,
      limit: limit || 100,
    },
  };
  qc_header.qc.parameters = {
    ...filter,
  };
  const operators: any = [];

  if (filter && filter.documentType) {
    operators.push(
      getOperator(
        "lower(esign_esign_email_config",
        "document_type)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%" + filter.documentType + "%")
          .toLowerCase(),
        "documentType"
      )
    );
  }

  if (filter && filter.messageType) {
    operators.push(
      getOperator(
        "lower(esign_esign_email_config",
        "message_type_code)",
        "LIKE",
        globalConfig.formatters
          .getEqualValue("%" + filter.messageType + "%")
          .toLowerCase(),
        "messageType"
      )
    );
  }

  if (operators.length > 0) {
    qc_header.qc.otherFilters = {
      expressions: [
        {
          and: {
            operators,
          },
        },
      ],
    };
  }

  return qc_header.qc;
}

export function searchEmailConfig(filter: any, offset?: any, limit?: any): Promise<any[]> {
  return new Promise<any[]>((resolve, reject) => {
    const params: Record<string, any> = {
      ...(filter || {}),
    };

    if (typeof offset === "number") params.offset = offset;
    if (typeof limit === "number") params.limit = limit;

    api()
      .get(`${basePathV2}/`, { params })
      .then((res: any) => {
        const data = Array.isArray(res?.data) ? res.data : (res?.data?.items || []);
        resolve(data);
      })
      .catch(() => {
        // Fallback to legacy query route while backend migration is in progress.
        const url = settings.api_query_url;
        const qc = getQcSearchEmailConfig(filter, offset, limit);
        const _headers = headers({
          qc: JSON.stringify(qc),
        });
        axios
          .get(url, {
            headers: _headers,
          })
          .then((res) => {
            resolve(res.data);
          })
          .catch((err) => reject(err));
      });
  });
}

export async function getEmailConfig(resourceUid: any) {
  return api().get(`${basePathV3}/${resourceUid}/`);
}

export async function createEmailConfig(emailConfig: any) {
  const payload = normalizeTemplatePayload(emailConfig);
  const response = await api().post(`${basePathV3}/`, payload);
  try {
    await syncDefaultTemplateState(payload, response?.data);
  } catch (e) {
    console.warn("Default template saved, but e-sign sync failed:", e);
  }
  return response;
}

export async function updateEmailConfig(resourceUid: any, emailConfig: any, options?: { syncDefault?: boolean }) {
  const payload = {
    ...normalizeTemplatePayload(emailConfig),
    resourceUid,
  };
  const response = await api().put(`${basePathV3}/${resourceUid}/`, payload);
  if (options?.syncDefault !== false) {
    try {
      await syncDefaultTemplateState(payload, response?.data);
    } catch (e) {
      console.warn("Default template saved, but e-sign sync failed:", e);
    }
  }
  return response;
}

export async function deleteEmailConfig(resourceUid: any) {
  let templateUid = String(resourceUid || "").trim();

  try {
    const templateRes = await getEmailConfig(resourceUid);
    const templateData = templateRes?.data;
    templateUid = getTemplateResourceUid(templateData, templateUid);
  } catch (_e) {
    // ignore pre-read failure and proceed with template deletion
  }

  const esignId = await getEsignIdByEmailTemplateId(templateUid);
  if (esignId) {
    await deleteEsignById(esignId).catch(async () => {
      await deleteEsignByEmailTemplateId(templateUid).catch(() => {
        // keep template deletion successful even if e-sign deletion fails
      });
    });
  }

  const response = await api().delete(`${basePathV2}/${resourceUid}/`);

  return response;
}
